import React, { useState, useEffect } from "react";
import "./styles/base.css";
import "./styles/adult.css";

export default function AdultForm({ age, onComplete }) {
  const STORAGE_KEY = "adultForm_v1";

  const defaultInitial = {
    age: age || "",
    firstName: "",
    lastName: "",
    dob: "",
    email: "",
    phone: "",
    handicapAccessible: false,
    roomingWithOthers: "",
    roommates: "",
    emergency1Name: "",
    emergency1Phone: "",
    emergency2Name: "",
    emergency2Phone: "",
    hasAllergies: "",
    allergies: "",
    allergyAcknowledged: false,

    // honeypot (should remain empty)
    website: "",
  };

  const [formData, setFormData] = useState(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      return saved ? { ...defaultInitial, ...JSON.parse(saved) } : defaultInitial;
    } catch {
      return defaultInitial;
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(formData));
    } catch {
      // ignore
    }
  }, [formData]);

  function formatDobMMDDYYYY(input) {
    const digits = String(input || "").replace(/\D/g, "").slice(0, 8);
    const mm = digits.slice(0, 2);
    const dd = digits.slice(2, 4);
    const yyyy = digits.slice(4, 8);
    if (digits.length <= 2) return mm;
    if (digits.length <= 4) return `${mm}/${dd}`;
    return `${mm}/${dd}/${yyyy}`;
  }

  function parseDOBtoDate(dobStr) {
    if (!dobStr) return null;
    const s = String(dobStr).trim();

    // MM/DD/YYYY only (exactly 2 digits / 2 digits / 4 digits)
    const m = s.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
    if (!m) return null;

    const [, mm, dd, yyyy] = m;
    const dt = new Date(Number(yyyy), Number(mm) - 1, Number(dd));
    if (Number.isNaN(dt.getTime())) return null;

    // Reject impossible dates like 02/31/2020 (JS would otherwise "roll over")
    if (
      dt.getFullYear() !== Number(yyyy) ||
      dt.getMonth() !== Number(mm) - 1 ||
      dt.getDate() !== Number(dd)
    ) {
      return null;
    }

    return dt;
  }

  function getAgeFromDOB(dobStr, now = new Date()) {
    const d = parseDOBtoDate(dobStr);
    if (!d) return null;
    let ageCalc = now.getFullYear() - d.getFullYear();
    const m = now.getMonth() - d.getMonth();
    if (m < 0 || (m === 0 && now.getDate() < d.getDate())) ageCalc--;
    return ageCalc;
  }
  // ------------------------------------------------

  function handleChange(e) {
    const { name, value, type, checked } = e.target;
    const next = type === "checkbox" ? checked : value;

    if (name === "dob") {
      const formatted = formatDobMMDDYYYY(next);
      setFormData((prev) => ({ ...prev, dob: formatted }));
      return;
    }
    if (name === "roomingWithOthers") {
      setFormData((prev) => ({
        ...prev,
        roomingWithOthers: next,
        roommates: next === "yes" ? prev.roommates : "",
      }));
      return;
    }

    if (name === "hasAllergies") {
      setFormData((prev) => ({
        ...prev,
        hasAllergies: next,
        allergies: next === "yes" ? prev.allergies : "",
        allergyAcknowledged: next === "yes" ? prev.allergyAcknowledged : false,
      }));
      return;
    }

    setFormData((prev) => ({ ...prev, [name]: next }));
  }

  function handleSubmit(e) {
    e.preventDefault();

    // Honeypot: bots fill this, humans won't
    if (String(formData.website || "").trim()) {
      // Fail silently to avoid giving bots feedback
      return;
    }

    // Validate allergy acknowledgement
    if (formData.hasAllergies === "yes" && !formData.allergyAcknowledged) {
      alert("Please acknowledge the allergy policy before continuing.");
      return;
    }

    // Validate DOB -> age >= 24
    const computedAge = getAgeFromDOB(formData.dob);
    if (computedAge === null) {
      alert("Please enter a valid date of birth (YYYY-MM-DD or MM/DD/YYYY).");
      return;
    }
    if (computedAge < 24) {
      alert("This adult form is for ages 24 and up. Please use the appropriate form.");
      return;
    }

    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch {
      // ignore
    }

    // Require roommates when roomingWithOthers === "yes"
    if (String(formData.roomingWithOthers).toLowerCase() === "yes" && !String(formData.roommates || "").trim()) {
      alert("Please list the names you will be sharing a room with.");
      return;
    }
    // Add computed age into formData.age so the backend can use it if desired
    // Keep honeypot field so the server can also reject bot submissions.
    const finalData = { ...formData, age: String(computedAge) };

    onComplete(finalData);
  }

  return (
    <form className="form-container adult-form" onSubmit={handleSubmit} autoComplete="on" noValidate>
      {/* Honeypot: hidden field to catch bots (should stay empty) */}
      <div style={{ position: "absolute", left: "-10000px", top: "auto", width: 1, height: 1, overflow: "hidden" }}>
        <label>
          Website
          <input
            type="text"
            name="website"
            value={formData.website}
            onChange={handleChange}
            autoComplete="off"
            tabIndex={-1}
          />
        </label>
      </div>

      <header>
        <h2>Adult Registration</h2>
        <p className="muted small">Complete the registrant information. Fields marked * are required.</p>
      </header>

      {/* First + Last side-by-side */}
      <div className="two-col">
        <div className="field">
          <label className="field-label">
            First Name <span className="required">*</span>
          </label>
          <input
            name="firstName"
            className="input"
            value={formData.firstName}
            onChange={handleChange}
            required
            autoComplete="given-name"
            autoCapitalize="words"
            spellCheck="false"
          />
        </div>

        <div className="field">
          <label className="field-label">
            Last Name <span className="required">*</span>
          </label>
          <input
            name="lastName"
            className="input"
            value={formData.lastName}
            onChange={handleChange}
            required
            autoComplete="family-name"
            autoCapitalize="words"
            spellCheck="false"
          />
        </div>
      </div>

      {/* DOB + Phone on one row */}
      <div className="two-col">
        <div className="field">
          <label className="field-label">
            Date of Birth <span className="required">*</span>
          </label>
          <input
            type="text"
            name="dob"
            className="input"
            value={formData.dob}
            onChange={handleChange}
            required
            placeholder="MM/DD/YYYY"
            inputMode="numeric"
            autoComplete="bday"
            maxLength={10}
          />
        </div>

        <div className="field">
          <label className="field-label">
            Phone <span className="required">*</span>
          </label>
          <input
            type="tel"
            name="phone"
            className="input"
            value={formData.phone}
            onChange={handleChange}
            required
            autoComplete="tel"
            inputMode="tel"
            placeholder="e.g. 555-555-5555"
          />
        </div>
      </div>

      {/* Email full width */}
      <div className="field">
        <label className="field-label">
          Email <span className="required">*</span>
        </label>
        <input
          type="email"
          name="email"
          className="input"
          value={formData.email}
          onChange={handleChange}
          required
          autoComplete="email"
          placeholder="you@example.com"
        />
      </div>

      <div className="field">
        <label className="field-label">Do you require handicap accessible room?</label>
        <label style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <input
            type="checkbox"
            name="handicapAccessible"
            checked={!!formData.handicapAccessible}
            onChange={handleChange}
          />
          Yes
        </label>
      </div>

      <div className="field">
        <label className="field-label">
          Will you be rooming with others? <span className="required">*</span></label>
        <select
          name="roomingWithOthers"
          className="select"
          value={formData.roomingWithOthers}
          onChange={handleChange}
          required
        >
          <option value="">Select</option>
          <option value="yes">Yes</option>
          <option value="no">No</option>
        </select>
      </div>

      {formData.roomingWithOthers === "yes" && (
        <div className="field">
          <label className="field-label">
            Please list the names you will be sharing a room with. <span className="required">*</span>
          </label>
          <input
            name="roommates"
            className="input"
            value={formData.roommates}
            onChange={handleChange}
            required
            placeholder="First & Last Names"
          />
        </div>
      )}

      {String(formData.roomingWithOthers).toLowerCase() === "no" && (
        <div className="note" style={{ marginTop: 8 }}>
          <p className="muted" style={{ margin: 0 }}>
            Please be aware; due to limited space single occupant campers may be subject to sharing sleeping quarters with other single occupant campers.
          </p>
        </div>
      )}

      <h3>Emergency Contact 1</h3>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: 12 }}>
        <div className="field">
          <label className="field-label">
            Name <span className="required">*</span>
          </label>
          <input
            name="emergency1Name"
            className="input"
            value={formData.emergency1Name}
            onChange={handleChange}
            required
            autoComplete="name"
            placeholder="First & Last Name"
          />
        </div>

        <div className="field">
          <label className="field-label">
            Phone <span className="required">*</span>
          </label>
          <input
            name="emergency1Phone"
            className="input"
            value={formData.emergency1Phone}
            onChange={handleChange}
            required
            autoComplete="tel"
            inputMode="tel"
            placeholder="e.g. 555-555-5555"
          />
        </div>
      </div>

      <h3>Emergency Contact 2</h3>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: 12 }}>
        <div className="field">
          <label className="field-label">Name</label>
          <input
            name="emergency2Name"
            className="input"
            value={formData.emergency2Name}
            onChange={handleChange}
            autoComplete="name"
            placeholder="First & Last Name"
          />
        </div>

        <div className="field">
          <label className="field-label">Phone</label>
          <input
            name="emergency2Phone"
            className="input"
            value={formData.emergency2Phone}
            onChange={handleChange}
            autoComplete="tel"
            inputMode="tel"
            placeholder="e.g. 555-555-5555"
          />
        </div>
      </div>

      {/* Allergies question + inline details + acknowledgement */}
      <div className="field">
        <label className="field-label">
          Do you have any allergies? <span className="required">*</span>
        </label>
        <select
          name="hasAllergies"
          className="select"
          value={formData.hasAllergies}
          onChange={handleChange}
          required
        >
          <option value="">Select</option>
          <option value="yes">Yes</option>
          <option value="no">No</option>
        </select>
      </div>

      {formData.hasAllergies === "yes" && (
        <div className="field allergy-block">
          <label className="field-label">
            What are your allergies? <span className="required">*</span>
          </label>
          <input
            name="allergies"
            className="input"
            value={formData.allergies}
            onChange={handleChange}
            required
            placeholder="e.g. Peanuts, Penicillin"
          />

          <div className="allergy-notice" role="note" aria-live="polite" style={{ marginTop: 10 }}>
            <p className="muted" style={{ margin: 0 }}>
              Please note: we cannot guarantee there will not be cross contamination of food allergens.
            </p>

            <label style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 8 }}>
              <input
                type="checkbox"
                name="allergyAcknowledged"
                checked={!!formData.allergyAcknowledged}
                onChange={handleChange}
              />
              I acknowledge and agree to the allergy policy above.
            </label>
          </div>
        </div>
      )}

      <div className="form-footer">
        <button type="submit" className="btn btn-primary">
          Next
        </button>
      </div>
    </form>
  );
}